<template>
  <section aria-label="Customer activity" :aria-busy="loading">
    <header class="activity-heading">
      <div>
        <h3>Activity</h3>
        <p>Who changed what, and when. Open an entry to compare the saved values.</p>
      </div>
      <b-button variant="outline-secondary" :disabled="loading" @click="load">Refresh</b-button>
    </header>
    <form class="activity-filters" @submit.prevent="applyFilters">
      <label>Show<select v-model="category" class="form-select" @change="applyFilters"><option value="all">All activity</option><option value="prices">Price changes</option></select></label>
      <label>From <input v-model="dateFrom" class="form-control" type="date" /></label>
      <label>To <input v-model="dateTo" class="form-control" type="date" :min="dateFrom" /></label>
      <b-button type="submit" variant="outline-secondary" :disabled="loading">Apply dates</b-button>
      <span class="activity-timezone">Times shown in {{ timezone }}</span>
    </form>
    <p class="activity-notice">
      Detailed history starts when activity logging is enabled. Passwords and credentials are hidden.
    </p>
    <p v-if="error" role="alert">
      {{ error }} <button type="button" class="btn btn-link" @click="load">Try again</button>
    </p>
    <p v-else-if="loading" role="status">Loading activity…</p>
    <p v-else-if="!items.length" class="activity-empty">
      No recorded {{ category === 'prices' ? 'price changes' : 'changes' }}{{ dateFrom || dateTo ? ' in this date range' : ' yet' }}.
    </p>
    <div v-else class="activity-list">
      <div class="activity-columns" aria-hidden="true">
        <span></span><span>Date &amp; time</span><span>Changed by</span><span>Change</span>
      </div>
      <details v-for="item in items" :key="item.id" class="activity-entry">
        <summary>
          <time :datetime="item.createdAt">{{ date(item.createdAt) }}</time>
          <span class="activity-actor"><strong>{{ item.actorName }}</strong><small v-if="item.actorEmail !== item.actorName">{{ item.actorEmail }}</small></span>
          <span><strong>{{ summary(item) }}</strong><small>{{ item.changes.reduce((n, change) => n + change.fields.length, 0) }} changed
            {{ item.changes.reduce((n, change) => n + change.fields.length, 0) === 1 ? 'field' : 'fields' }} · View
            details</small></span>
        </summary>
        <div class="activity-details">
          <section v-for="(change, index) in item.changes" :key="index" class="activity-change">
            <h4>
              {{ change.entity }} <span class="activity-action">{{ change.action }}</span>
            </h4>
            <dl class="activity-context">
              <div v-for="(value, key) in change.context" :key="key">
                <dt>{{ label(String(key)) }}</dt>
                <dd>{{ key === 'carrier_handle' ? CARRIERS_NAMES[String(value)] || value : value }}</dd>
              </div>
            </dl>
            <div class="activity-table-scroll">
              <table class="table">
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Before</th>
                    <th scope="col">After</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="field in change.fields" :key="field.field">
                    <th scope="row">{{ label(field.field) }}</th>
                    <td>
                      <pre>{{ format(field.before, field.field) }}</pre>
                    </td>
                    <td>
                      <pre>{{ format(field.after, field.field) }}</pre>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </details>
    </div>
    <footer v-if="count > 20" class="activity-pagination">
      <b-button variant="outline-secondary" :disabled="page === 1 || loading" @click="changePage(-1)">
        Previous
      </b-button>
      <span>Page {{ page }} of {{ Math.ceil(count / 20) }} · {{ count }} entries</span>
      <b-button variant="outline-secondary" :disabled="page * 20 >= count || loading" @click="changePage(1)">
        Next
      </b-button>
    </footer>
  </section>
</template>

<script setup lang="ts">
import type { CustomerActivityItem, CustomerActivityCategory } from '@/types/Models/CustomerActivity'
import { onBeforeUnmount, ref, watch } from 'vue'
import { CARRIERS_NAMES } from '@/const/Carriers'
import CustomerActivityProxy from '@/proxies/Customer/CustomerActivityProxy'

const props = defineProps<{ companyId: string; initialCategory?: CustomerActivityCategory }>()
const category = ref<CustomerActivityCategory>(props.initialCategory ?? 'all')
const items = ref<CustomerActivityItem[]>([])
const count = ref(0)
const loading = ref(false)
const error = ref('')
const page = ref(1)
const dateFrom = ref('')
const dateTo = ref('')
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
let requestId = 0
const label = (value: string) =>
  value === 'carrier_handle'
    ? 'Carrier'
    : value === 'min_packages_amount'
      ? 'Minimum quantity'
      : value.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase())
const date = (value: string) =>
  new Intl.DateTimeFormat('sv-SE', { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value))
const format = (value: unknown, field = ''): string =>
  /^(is_|has_|hide_|should_)/.test(field) && (value === 0 || value === 1)
    ? value
      ? 'Yes'
      : 'No'
    : value === null || value === undefined
      ? 'Not set'
      : typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : typeof value === 'object'
          ? JSON.stringify(value, null, 2)
          : String(value)
const summary = (item: CustomerActivityItem) =>
  [
    ...new Set(
      item.changes.map((change) =>
        [
          change.entity,
          change.context.carrier_handle ? CARRIERS_NAMES[String(change.context.carrier_handle)] : '',
          ...change.fields.slice(0, 2).map((field) => label(field.field)),
        ]
          .filter(Boolean)
          .join(' · '),
      ),
    ),
  ]
    .slice(0, 2)
    .join('; ')
async function load() {
  const id = ++requestId
  loading.value = true
  error.value = ''
  try {
    const response = await new CustomerActivityProxy(props.companyId, {
      page: page.value,
      category: category.value,
      dateFrom: dateFrom.value,
      dateTo: dateTo.value,
      timezone,
    }).all()
    if (id !== requestId) return
    items.value = response.data ?? []
    count.value = response.count ?? 0
  } catch {
    if (id === requestId) error.value = 'Activity could not be loaded. Please try again.'
  } finally {
    if (id === requestId) loading.value = false
  }
}
function applyFilters() {
  page.value = 1
  load()
}
function changePage(delta: number) {
  page.value += delta
  load()
}
watch(
  () => props.companyId,
  () => {
    items.value = []
    page.value = 1
    load()
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  requestId++
})
</script>

<style scoped>
.activity-heading,
.activity-filters,
.activity-pagination {
  display: flex;
  gap: 16px;
  align-items: end;
  flex-wrap: wrap;
}
.activity-heading {
  justify-content: space-between;
  align-items: start;
}
h3 {
  font-size: 20px;
  font-weight: 600;
}
.activity-heading p,
.activity-notice,
.activity-timezone {
  color: #596560;
}
.activity-filters {
  margin: 20px 0 12px;
}
.activity-filters > button { min-height: 44px; }
.activity-filters label {
  margin-bottom: 0;
  display: grid;
  gap: 6px;
  font-size: 14px;
}
.activity-timezone,
.activity-notice {
  font-size: 13px;
}
.activity-timezone {
  margin-left: auto;
}
.activity-entry {
  background: white;
  border: 1px solid #dce3e0;
  border-radius: 6px;
  margin-top: 10px;
}
summary,
.activity-columns {
  display: grid;
  grid-template-columns: 24px minmax(160px, 0.8fr) minmax(180px, 1fr) 1.5fr;
  align-items: center;
  gap: 16px;
  padding: 18px;
  cursor: pointer;
}
.activity-columns {
  padding-block: 6px;
  color: #596560;
  font-size: 13px;
}
summary::before {
  content: '›';
  font-size: 24px;
}
details[open] > summary::before {
  transform: rotate(90deg);
}
summary::-webkit-details-marker {
  display: none;
}
summary:focus-visible {
  outline: 2px solid #00897e;
  outline-offset: 2px;
}
summary time,
summary strong {
  font-size: 14px;
}
summary small {
  display: block;
  margin-top: 4px;
  color: #596560;
  overflow-wrap: anywhere;
}
.activity-details {
  border-top: 1px solid #dce3e0;
  padding: 20px;
}
.activity-change + .activity-change {
  margin-top: 28px;
}
h4 {
  font-size: 16px;
  font-weight: 600;
}
.activity-action {
  font-size: 12px;
  font-weight: 400;
  color: #596560;
  margin-left: 8px;
}
.activity-context {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin: 12px 0;
  font-size: 13px;
}
.activity-context dt {
  color: #596560;
  font-weight: 400;
}
.activity-context dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.activity-table-scroll {
  overflow-x: auto;
}
table {
  table-layout: fixed;
  min-width: 480px;
  margin: 0;
}
th,
td {
  width: 33.33%;
  overflow-wrap: anywhere;
}
pre {
  font: inherit;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  margin: 0;
}
.activity-pagination {
  justify-content: flex-end;
  margin-top: 20px;
}
.activity-empty {
  padding: 32px;
  text-align: center;
  background: white;
  border: 1px solid #dce3e0;
  border-radius: 6px;
}
@media (max-width: 850px) {
  .activity-columns {
    display: none;
  }
  summary {
    grid-template-columns: 16px 1fr;
    gap: 8px;
  }
  summary > span {
    grid-column: 2;
  }
  .activity-timezone {
    margin-left: 0;
  }
}
</style>
